import type { ReactNode } from "react";

/** Flat, divider-separated list container. */
function Root({ children }: { children: ReactNode }) {
  return <div className="divide-y divide-border overflow-hidden rounded-lg border">{children}</div>;
}

function Empty({ children }: { children: ReactNode }) {
  return <div className="px-4 py-12 text-center text-sm text-muted-foreground">{children}</div>;
}

function RowFrame({ children }: { children: ReactNode }) {
  return (
    <div className="group relative flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted/40">
      {children}
    </div>
  );
}

/** A row whose action is not navigation — it carries no link of its own. */
function StaticRow({ children }: { children: ReactNode }) {
  return <RowFrame>{children}</RowFrame>;
}

export type RowLinkRenderer = (props: { className: string; "aria-label": string }) => ReactNode;

interface NavigatingRowProps {
  /** Whole-row navigation target. */
  renderLink: RowLinkRenderer;
  /**
   * Accessible name for the stretched link. Required rather than optional
   * because the link covers the row and has no text of its own: a row that
   * forgets it reads as a blank link, and nothing else would catch that.
   */
  label: string;
  children: ReactNode;
}

/** A row the whole surface of which navigates, via a link stretched over it. */
function NavigatingRow({ renderLink, label, children }: NavigatingRowProps) {
  return (
    <RowFrame>
      {renderLink({ className: "absolute inset-0", "aria-label": label })}
      {children}
    </RowFrame>
  );
}

/** Leading cell: takes the row's slack so the trailing cells stay put. */
function Primary({ children }: { children: ReactNode }) {
  return <div className="min-w-0 flex-1">{children}</div>;
}

function Title({ children }: { children: ReactNode }) {
  return <div className="truncate text-sm font-semibold leading-snug">{children}</div>;
}

/**
 * The optional cells render nothing when they have nothing, so a caller can
 * pass a value that may be absent without leaving an empty box behind — the
 * subtitle's own top margin is enough to shift the row it sits in.
 */
function Subtitle({ children }: { children: ReactNode }) {
  if (children == null) return null;
  return <div className="mt-0.5 truncate text-xs text-muted-foreground">{children}</div>;
}

/** Status cell — typically a <Badge />. */
function Status({ children }: { children: ReactNode }) {
  if (children == null) return null;
  return <div className="shrink-0">{children}</div>;
}

/** Right-aligned secondary text (e.g. a date). */
function Meta({ children }: { children: ReactNode }) {
  if (children == null) return null;
  return <div className="hidden shrink-0 text-right text-xs text-muted-foreground md:block">{children}</div>;
}

/**
 * Trailing actions (e.g. a kebab menu). Stacked above a navigating row's
 * stretched link, so opening the menu doesn't navigate instead.
 */
function Actions({ children }: { children: ReactNode }) {
  return <div className="relative z-10 shrink-0">{children}</div>;
}

export const DataList = Object.assign(Root, {
  Empty,
  StaticRow,
  NavigatingRow,
  Primary,
  Title,
  Subtitle,
  Status,
  Meta,
  Actions,
});
